// @flow
import { emitter } from "common/emitter";
import { View } from "extensions/marionette";
import { CollectionView } from "backbone.marionette";

import Radio from "backbone.radio";
import $ from "jquery";
import "typeahead.js/dist/typeahead.jquery";
import { escape } from "lodash-es";

import strings from "common/strings";
import { store } from "common/store";

import {
    SET_BU_REF,
    SET_MERCHANT_REF
} from "common/actions/common";

import { references } from "common/helpers/references";
import { Bloodhound } from "common/components/typeahead";
import { fadeInText } from "common/helpers/effects";
import { getDimensionHandler } from "common/helpers/dimensionHelpers";

import type { TypeaheadSourceDataType } from "common/helpers/dimensionHelpers";

import { exportBUs } from "../businessUnitManager/buExporter";

import { BusinessUnitTree } from "./buTree";
import businessUnitTemplate from "./businessUnitSelector.jst";
import NodeViewTemplate from "./bu.jst";

export const NodeView = View.extend({
    template: NodeViewTemplate,
    className: "node",
    
    events: {
        "click #buTree": (e) => {
            e.stopPropagation();
            e.preventDefault();
        }
    },
    
    childViewTriggers: {
        "change:bu": "node:change:bu"
    },
    
    regions: {
        children: {
            el: ".children",
            replaceElement: true
        }
    },
    
    initialize() {
        this.collection = this.model.children;
    },
    
    onRender() {
        // Show child nodes if they are present
        if (this.model.hasChildren()) {
            const treeView = new BusinessUnitTree({
                collection: this.model.children
            });
            this.showChildView("children", treeView);
        }
    },
    
    templateContext() {
        const popoverMsg = store.getState().common.user_permissions.has_bu_manager ? `${strings.unidentifiedPopover} ${strings.unidentifiedPopoverManager}` : strings.unidentifiedPopover;
        return {
            overrideDisplayName: this.getOption("firstLevel") && this.getOption("useAllLevels") && strings.allLevels,
            hasChildren: this.model.hasChildren(),
            popover: this.model.get("display_name") === strings.unidentified ? popoverMsg : false
        };
    }
});

export const TreeView = CollectionView.extend({
    className: "units-list",
    childView: NodeView,
    childViewOptions() {
        return {
            firstLevel: this.getOption("firstLevel") || false,
            useAllLevels: this.getOption("useAllLevels"),
            selectedModel: this.getOption("model")
        };
    },
    childViewTriggers: {
        "node:change:bu": "node:change:bu"
    }
});

export const RootView = View.extend({
    template: businessUnitTemplate,
    regions: {
        list: ".business-units"
    },
    ui: {
        typeaheadCont: ".typeahead-cont input.typeahead",
        selectedName: ".selected-unit-name",
        selectedPath: ".selected-unit-path"
    },
    events: {
        "shown.bs.dropdown": function() {
            this.toggleNoScrollOnBody(true);
            this.focusTimeout = window.setTimeout(() => this.ui.typeaheadCont.focus(), 200);
            this.highlightSelectedNodeInTree();
        },
        "hidden.bs.dropdown": function() {
            this.toggleNoScrollOnBody(false);
            if (this.focusTimeout) {
                clearTimeout(this.focusTimeout);
            }
        },
        "click a[data-nodeid]": function(e) {
            e.preventDefault();
            const unit = e.currentTarget.dataset.nodeid;
            if (unit) {
                this.setActiveUnit(unit);
            } else {
                throw new Error("Cannot switch to business unit. Requested node does not have required attribute `data-nodeid`.");
            }
        },
        "click div.export": exportBUs,
        "click div.manager": "goToManager"
    },
    childViewEvents: {
        "node:change:bu": "setActiveUnitFromTree"
    },
    initialize() {
        // Make sure that noScroll is off on logout
        emitter.on("logout:before", this.addNoScroll.bind(this));
    },
    
    templateContext() {
        return {
            isBUExport: store.getState().common.user_permissions.has_bu_export,
            hasBUManage: store.getState().common.user_permissions.has_bu_manager
        };
    },
    
    goToManager(e) {
        e.preventDefault();
        Radio.channel("main").request("navigate", "preferences/bumanager");
    },
    
    filter(...args) {
        return this.getChildView("list").setFilter(...args);
    },
    
    sort() {
        return this.getChildView("list").collection.sort();
    },
    
    addNoScroll() {
        this.toggleNoScrollOnBody(false);
    },
    
    toggleNoScrollOnBody(add) {
        $("body").toggleClass("noscroll", add);
    },
    
    setActiveUnitFromTree(model) {
        this.setActiveUnit(model, true);
    },
    
    setActiveUnit(activeUnitModel, fromEvent = false) {
        const { selectedTree } = Radio.channel("main").request("get:user").getSelectedNodeWithTree();

        if (typeof activeUnitModel === "string") {
            // If passed a string than search merchant tree for a node with provided id
            const selectedNode = selectedTree.getChildNodeWithItsTree(activeUnitModel, { includeParent: true });

            if (activeUnitModel) {
                activeUnitModel = selectedNode.at(0);
            } else {
                throw new Error(`Unable to find a node for the requested business unit ${activeUnitModel}`);
            }
        }
        if (fromEvent) {
            this.ui.selectedPath.removeClass("hidden").html("<div class='loader line-1 text-input__loading--line short'></div>");
            this.ui.selectedName.html("<div class='loader line-2 text-input__loading--line medium'></div>");
        } else {
            this.updateSelectedUnit({ selectedNode: activeUnitModel, selectedTree });
        }
        
        if (activeUnitModel.id === store.getState().common.merchant_ref) {
            store.dispatch(SET_MERCHANT_REF(activeUnitModel.id));
        } else {
            store.dispatch(SET_BU_REF(activeUnitModel.id));
        }
    
        this.toggleNoScrollOnBody(false);
    
        // Close dropdown
        this.$el.removeClass("open");
    },
    
    updateSelectedUnit({ selectedNode, selectedTree } = Radio.channel("main").request("get:user").getSelectedNodeWithTree()) {
        let nameToShow = selectedNode.get("display_name");
        if (selectedNode.id === selectedTree.at(0).id) {
            nameToShow = strings.allLevels;
        }
        
        fadeInText(this.ui.selectedName, escape(nameToShow));
        
        const path = this.getHierarchyPath({ selectedNode, selectedTree });
        if (path) {
            this.ui.selectedPath.removeClass("hidden");
            fadeInText(this.ui.selectedPath, escape(path), { out: 100, "in": 300 }, (elt) => {
                elt.attr("title", elt[0].offsetWidth < elt[0].scrollWidth ? path.replace("&gt;", ">") : "");
            });
        } else {
            this.ui.selectedPath.addClass("hidden");
        }
    },
    
    highlightSelectedNodeInTree() {
        const topIcon = this.$el.find(".units-list .node .all-icon");
        const topLabel = this.$el.find(".units-list .node a:first");
        
        if (store.getState().common.bu_ref) {
            topIcon.hide();
            topLabel.removeClass("selected");
        } else {
            topIcon.show();
            topLabel.addClass("selected");
        }
    },
    
    getAllSearchableBUs() {
        // Get typeahead options, but use bu-picker template instead the standard one in handlers
        return {
            ...getDimensionHandler("bu")({}),
            templates_suggestion: (data) => `<div>${data.alias}<br/><em>${data.parent || ""}</em></div>`
        };
    },
    
    addTypeahead() {
        const allUnits = this.getAllSearchableBUs();
        const goToBU = (e, obj) => {
            const match = this.getOption("available").getChildNodeWithItsTree(obj.value, { includeParent: true });
            if (match && match.length) {
                this.setActiveUnit(match.at(0));
            } else {
                throw new Error("Cannot find matched BU from typeahead");
            }
        };
        
        this.ui.typeaheadCont.typeahead(null, {
            name: "BUs",
            display: allUnits.display[0],
            source: new Bloodhound({
                datumTokenizer: allUnits.datumTokenizer,
                queryTokenizer: Bloodhound.tokenizers.whitespace,
                local: allUnits.data,
                identify: (obj: TypeaheadSourceDataType): string => obj.value,
                sufficient: 6
            }),
            limit: 6,
            templates: {
                empty: `<div class='empty-message'>${strings.preferences.typeahead.emptyMessage}</div>`,
                suggestion: allUnits.templates_suggestion
            }
        });
        
        this.ui.typeaheadCont.on("typeahead:render", () => {
            this.getChildView("list").$el.addClass("hidden");
        });
        this.ui.typeaheadCont.on("keyup", () => {
            if (!this.ui.typeaheadCont.val()) {
                this.getChildView("list").$el.removeClass("hidden");
            }
        });
        this.ui.typeaheadCont.on("typeahead:close", () => {
            this.ui.typeaheadCont.typeahead("val", "");
            this.getChildView("list").$el.removeClass("hidden");
        });
        this.ui.typeaheadCont.on("typeahead:autocomplete", goToBU);
        this.ui.typeaheadCont.on("typeahead:select", goToBU);
    },
    
    getHierarchyPath({ selectedNode, selectedTree }): string {
        if (selectedNode.id === selectedTree.at(0).id) {
            return "";
        }
    
        // $FlowFixMe Another issue that we should solve by creating a separate toAlias func with different flow signature
        const { parent = "" } = references.toAlias(selectedNode.id, "bu", { full: true });
        
        if (parent.length > 66) {
            return parent;
        }
        return `${strings.allLevels} > ${parent}`;
    },
    
    onRender() {
        this.showChildView("list", new TreeView({
            model: this.model,
            collection: this.getOption("available"),
            firstLevel: true,
            useAllLevels: this.getOption("useAllLevels") || true
        }));
        
        this.addTypeahead();
        
        // Mark selected item in the list (initial)
        this.updateSelectedUnit();
    },
    
    onBeforeDestroy() {
        if (this.ui.typeaheadCont && this.ui.typeaheadCont.typeahead) {
            this.ui.typeaheadCont.typeahead("destroy");
        }
        
        if (this.focusTimeout) {
            clearTimeout(this.focusTimeout);
        }
    
        emitter.off("logout:before", this.addNoScroll.bind(this));
        
        this.toggleNoScrollOnBody(false);
    }
});
